import { useMemo } from 'react';
import type { PolicyVersionSummary } from 'used-vehicle-contracts';
import { Button } from '../../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../../components/ui/DataTable';
import { StatusText, type StatusTone } from '../../../components/ui/StatusText';
import { formatDay } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

const STATUS_TONES: Record<string, StatusTone> = {
  PUBLISHED: 'success',
  DRAFT: 'info',
  ARCHIVED: 'muted',
};

export interface PolicyVersionsTableProps {
  versions: readonly PolicyVersionSummary[];
  isLoading: boolean;
  isOpening: boolean;
  onOpenAsDraft: (versionId: string) => void;
}

/** Every version the tenant has had, and the one action a past version still offers. */
export const PolicyVersionsTable = ({ versions, isLoading, isOpening, onOpenAsDraft }: PolicyVersionsTableProps) => {
  const { t } = useI18nTranslation();

  const columns = useMemo<DataTableColumns<PolicyVersionSummary>>(
    () => [
      {
        id: 'version',
        header: t('equations.versionColumn'),
        accessorFn: (row) => row.version,
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="font-mono text-sm tabular-nums text-foreground"
          >
            v{row.original.version}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('equations.statusColumn'),
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <StatusText
            label={t(`equations.statuses.${row.original.status}`)}
            tone={STATUS_TONES[row.original.status] ?? 'muted'}
          />
        ),
      },
      {
        id: 'publishedAt',
        header: t('equations.publishedColumn'),
        accessorFn: (row) => row.publishedAt ?? '',
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">
            <p>{row.original.publishedAt ? formatDay(row.original.publishedAt) : t('common.notRecorded')}</p>
            {row.original.publishedByName ? <p className="text-xs">{row.original.publishedByName}</p> : null}
          </div>
        ),
      },
      {
        id: 'reason',
        header: t('equations.reasonColumn'),
        accessorFn: (row) => row.reason ?? '',
        enableSorting: false,
        cell: ({ row }) => <p className="max-w-md text-sm text-muted-foreground">{row.original.reason ?? t('common.notRecorded')}</p>,
      },
      {
        id: 'actions',
        header: t('equations.actionsColumn'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status === 'DRAFT' ? null : (
            <Button
              variant="outline"
              size="sm"
              disabled={isOpening}
              onClick={() => onOpenAsDraft(row.original.id)}
            >
              {t('equations.openAsDraft')}
            </Button>
          ),
      },
    ],
    [isOpening, onOpenAsDraft, t],
  );

  return (
    <DataTable
      columns={columns}
      data={versions as PolicyVersionSummary[]}
      isLoading={isLoading}
      emptyMessage={t('equations.noVersions')}
      pageSize={10}
    />
  );
};
